import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, User, Building2, Clock, CheckCircle2, 
  XCircle, AlertTriangle, Shield, Calendar, MessageSquare,
  RefreshCw, ChevronRight, AlertCircle, Send,
  FileSignature, Scale, Handshake, Ban, Timer, Wallet,
  Eye, Home, ExternalLink, Lock, Unlock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import AEDIcon from '@/components/icons/AEDIcon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';
import DeadlinesMonitor from '@/components/DeadlinesMonitor';

// ============ CONSTANTS ============

const DEAL_STATE_CONFIG = {
  deal_none: { label: 'No Deal', color: 'bg-gray-600', icon: Clock, progress: 0 },
  deal_negotiating: { label: 'Negotiating', color: 'bg-blue-600', icon: MessageSquare, progress: 10 },
  deal_offer_accepted: { label: 'Offer Accepted', color: 'bg-green-600', icon: CheckCircle2, progress: 25 },
  deal_form_f_pending: { label: 'Form F Pending', color: 'bg-gold-600', icon: FileText, progress: 40 },
  deal_form_f_signed: { label: 'Form F Signed', color: 'bg-emerald-600', icon: FileSignature, progress: 60 },
  deal_extension_requested: { label: 'Extension Requested', color: 'bg-orange-600', icon: Timer, progress: 65 },
  deal_extension_accepted: { label: 'Extension Accepted', color: 'bg-teal-600', icon: Calendar, progress: 70 },
  deal_termination_requested: { label: 'Termination Requested', color: 'bg-red-600', icon: Ban, progress: 65 },
  deal_terminated_mutual: { label: 'Terminated (Mutual)', color: 'bg-gray-600', icon: Handshake, progress: 100 },
  deal_dispute_mode: { label: 'Dispute Mode', color: 'bg-red-700', icon: Scale, progress: 70 },
  deal_closed: { label: 'Closed', color: 'bg-green-700', icon: CheckCircle2, progress: 100 },
};

const DEPOSIT_STATUS_CONFIG = {
  held: { label: 'Held in Escrow', color: 'text-blue-400', bgColor: 'bg-blue-950/30', icon: Lock },
  release_requested: { label: 'Release Requested', color: 'text-gold-400', bgColor: 'bg-gold-950/30', icon: Unlock },
  mutual_instruction_signed: { label: 'Ready for Release', color: 'text-green-400', bgColor: 'bg-green-950/30', icon: CheckCircle2 },
  released: { label: 'Released', color: 'text-emerald-400', bgColor: 'bg-emerald-950/30', icon: AEDIcon },
  refunded: { label: 'Refunded', color: 'text-purple-400', bgColor: 'bg-purple-950/30', icon: RefreshCw },
  disputed: { label: 'Disputed', color: 'text-red-400', bgColor: 'bg-red-950/30', icon: AlertTriangle },
};

const EVENT_TYPE_CONFIG = {
  PROPERTY_OFFER_ACCEPTED: { label: 'Offer Accepted', icon: CheckCircle2, color: 'text-green-400' },
  DEAL_STATE_CHANGED: { label: 'State Changed', icon: RefreshCw, color: 'text-blue-400' },
  DEAL_FORM_F_SIGNED: { label: 'Form F Signed', icon: FileSignature, color: 'text-emerald-400' },
  EXTENSION_REQUESTED: { label: 'Extension Requested', icon: Timer, color: 'text-orange-400' },
  EXTENSION_ACCEPTED: { label: 'Extension Accepted', icon: CheckCircle2, color: 'text-green-400' },
  EXTENSION_REJECTED: { label: 'Extension Rejected', icon: XCircle, color: 'text-red-400' },
  EXTENSION_COUNTERED: { label: 'Extension Countered', icon: MessageSquare, color: 'text-gold-400' },
  TERMINATION_REQUESTED: { label: 'Termination Requested', icon: Ban, color: 'text-red-400' },
  TERMINATION_ACCEPTED: { label: 'Termination Accepted', icon: Handshake, color: 'text-gray-400' },
  TERMINATION_REJECTED: { label: 'Termination Rejected', icon: XCircle, color: 'text-red-400' },
  DISPUTE_OPENED: { label: 'Dispute Opened', icon: Scale, color: 'text-red-500' },
  DISPUTE_RESOLVED: { label: 'Dispute Resolved', icon: CheckCircle2, color: 'text-green-400' },
  DEPOSIT_RELEASE_REQUESTED: { label: 'Deposit Release Requested', icon: Wallet, color: 'text-gold-400' },
  DEPOSIT_INSTRUCTION_SIGNED: { label: 'Deposit Instruction Signed', icon: FileSignature, color: 'text-green-400' },
  DEAL_MESSAGE_SENT: { label: 'Message Sent', icon: MessageSquare, color: 'text-blue-400' },
};

const EXTENSION_REASONS = [
  { value: 'FINANCING_DELAY', label: 'Financing Delay' },
  { value: 'DOCUMENTATION_PENDING', label: 'Documentation Pending' },
  { value: 'INSPECTION_ISSUES', label: 'Inspection Issues' },
  { value: 'RELOCATION_TIMING', label: 'Relocation Timing' },
  { value: 'OTHER', label: 'Other' },
];

const TERMINATION_REASONS = [
  { value: 'BUYER_DEFAULT', label: 'Buyer Default' },
  { value: 'SELLER_DEFAULT', label: 'Seller Default' },
  { value: 'MUTUAL_AGREEMENT', label: 'Mutual Agreement' },
  { value: 'FINANCING_FAILED', label: 'Financing Failed' },
  { value: 'TITLE_ISSUES', label: 'Title Issues' },
  { value: 'OTHER', label: 'Other' },
];

const DISPUTE_REASONS = [
  { value: 'BREACH', label: 'Contract Breach' },
  { value: 'MISREPRESENTATION', label: 'Misrepresentation' },
  { value: 'DEPOSIT_DISPUTE', label: 'Deposit Dispute' },
  { value: 'TIMELINE_DISPUTE', label: 'Timeline Dispute' },
  { value: 'OTHER', label: 'Other' },
];

// ============ HELPER FUNCTIONS ============

const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelativeTime = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return date.toLocaleDateString('en-AE', { day: 'numeric', month: 'short' });
};

const formatCurrency = (amount) => {
  if (!amount) return 'AED 0';
  return `AED ${Number(amount).toLocaleString()}`;
};

const maskName = (name, role, viewerRole) => {
  if (viewerRole === 'admin') return name;
  return name || 'Participant';
};

// ============ COMPONENTS ============

const DealStateBadge = ({ state }) => {
  const config = DEAL_STATE_CONFIG[state] || DEAL_STATE_CONFIG.deal_none;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white px-3 py-1.5 text-sm font-medium`}>
      <Icon className="w-4 h-4 mr-2" />
      {config.label}
    </Badge>
  );
};

const DepositStatusBadge = ({ status }) => {
  const config = DEPOSIT_STATUS_CONFIG[status] || DEPOSIT_STATUS_CONFIG.held;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-2 ${config.color}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{config.label}</span>
    </div>
  );
};

const TimelineEvent = ({ event, onViewDetails }) => {
  const config = EVENT_TYPE_CONFIG[event.event_type] || { 
    label: event.event_type?.replace(/_/g, ' '), 
    icon: Clock, 
    color: 'text-gray-400' 
  };
  const Icon = config.icon;
  
  return (
    <div 
      className="relative flex gap-4 pb-8 last:pb-0 group cursor-pointer"
      onClick={() => onViewDetails?.(event)}
      data-testid={`timeline-event-${event.timestamp}`}
    >
      {/* Timeline line */}
      <div className="absolute left-[17px] top-10 bottom-0 w-px bg-white/10 group-last:hidden" />
      
      {/* Icon */}
      <div className={`relative z-10 w-9 h-9 rounded-full bg-background border-2 border-white/20 flex items-center justify-center ${config.color} group-hover:border-primary/50 transition-colors`}>
        <Icon className="w-4 h-4" />
      </div>
      
      {/* Content */}
      <div className="flex-1 pt-1">
        <div className="flex items-center justify-between">
          <span className={`font-medium ${config.color}`}>{config.label}</span>
          <span className="text-xs text-muted-foreground">{formatRelativeTime(event.timestamp)}</span>
        </div>
        
        <div className="text-sm text-muted-foreground mt-1">
          <span className="text-white/70">{event.actor?.role || 'System'}</span>
          {event.actor?.email && <span className="ml-2 opacity-50">({event.actor.email})</span>}
        </div>
        
        {event.state_transition?.from && event.state_transition?.to && (
          <div className="flex items-center gap-2 mt-2 text-xs">
            <Badge variant="outline" className="border-white/20 text-white/50">
              {event.state_transition.from?.replace('deal_', '').replace(/_/g, ' ')}
            </Badge>
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <Badge variant="outline" className="border-primary/50 text-primary">
              {event.state_transition.to?.replace('deal_', '').replace(/_/g, ' ')}
            </Badge>
          </div>
        )}
        
        {event.metadata?.reason && (
          <p className="text-xs text-muted-foreground mt-2 italic">
            "{event.metadata.reason}"
          </p>
        )}
      </div>
    </div>
  );
};

const ActionButton = ({ children, onClick, disabled, disabledReason, variant = 'default', icon: Icon, ...props }) => {
  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button 
                variant="outline" 
                disabled 
                className="w-full opacity-50 cursor-not-allowed"
                {...props}
              >
                {Icon && <Icon className="w-4 h-4 mr-2" />}
                {children}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{disabledReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <Button variant={variant} onClick={onClick} className="w-full" {...props}>
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {children}
    </Button>
  );
};

// ============ MODALS ============

const ExtensionModal = ({ open, onClose, onSubmit, loading }) => {
  const [form, setForm] = useState({
    requested_days: 14,
    reason_code: '',
    reason_text: '',
    compensation_type: 'FIXED_AMOUNT',
    compensation_amount: 0,
    proposed_completion_date: ''
  });
  
  const handleSubmit = () => {
    if (!form.reason_code) {
      toast.error('Please select a reason');
      return;
    }
    onSubmit(form);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-orange-400" />
            Request Extension
          </DialogTitle>
          <DialogDescription>
            Request additional time to complete the transaction. This will notify the counterparty.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Extension Duration</label>
            <div className="flex gap-2">
              <Input 
                type="number" 
                value={form.requested_days}
                onChange={(e) => setForm({ ...form, requested_days: parseInt(e.target.value) || 0 })}
                className="w-24"
                min={1}
                max={90}
              />
              <span className="text-muted-foreground self-center">days</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Select value={form.reason_code} onValueChange={(v) => setForm({ ...form, reason_code: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {EXTENSION_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Additional Details</label>
            <Textarea 
              value={form.reason_text}
              onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
              placeholder="Provide more context for the extension request..."
              rows={3}
            />
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Compensation Proposal (Optional)</label>
            <p className="text-xs text-muted-foreground">Offering compensation may increase approval chances.</p>
            <div className="flex gap-2">
              <Select value={form.compensation_type} onValueChange={(v) => setForm({ ...form, compensation_type: v })}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIXED_AMOUNT">Fixed Amount</SelectItem>
                  <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                  <SelectItem value="NONE">No Compensation</SelectItem>
                </SelectContent>
              </Select>
              {form.compensation_type !== 'NONE' && (
                <Input 
                  type="number"
                  value={form.compensation_amount}
                  onChange={(e) => setForm({ ...form, compensation_amount: parseFloat(e.target.value) || 0 })}
                  placeholder={form.compensation_type === 'PERCENTAGE' ? '%' : 'AED'}
                  className="flex-1"
                />
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TerminationModal = ({ open, onClose, onSubmit, loading }) => {
  const [form, setForm] = useState({
    reason_code: '',
    reason_text: '',
    proposed_settlement: {
      forfeit_deposit_percentage: 0,
      note: ''
    }
  });
  
  const handleSubmit = () => {
    if (!form.reason_code) {
      toast.error('Please select a reason');
      return;
    }
    onSubmit(form);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" />
            Request Termination
          </DialogTitle>
          <DialogDescription>
            Request to terminate the transaction. The counterparty must agree, or this may escalate to dispute.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-red-950/20 border border-red-900/30">
            <p className="text-sm text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Termination may result in deposit forfeiture based on contract terms.
            </p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason for Termination</label>
            <Select value={form.reason_code} onValueChange={(v) => setForm({ ...form, reason_code: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {TERMINATION_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Detailed Explanation</label>
            <Textarea 
              value={form.reason_text}
              onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
              placeholder="Explain the circumstances leading to termination request..."
              rows={3}
            />
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Settlement Proposal (Optional)</label>
            <p className="text-xs text-muted-foreground">Propose how the deposit should be handled.</p>
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground">Forfeit</span>
              <Input 
                type="number"
                value={form.proposed_settlement.forfeit_deposit_percentage}
                onChange={(e) => setForm({ 
                  ...form, 
                  proposed_settlement: { 
                    ...form.proposed_settlement, 
                    forfeit_deposit_percentage: parseInt(e.target.value) || 0 
                  }
                })}
                className="w-20"
                min={0}
                max={100}
              />
              <span className="text-sm text-muted-foreground">% of deposit</span>
            </div>
            <Textarea 
              value={form.proposed_settlement.note}
              onChange={(e) => setForm({ 
                ...form, 
                proposed_settlement: { ...form.proposed_settlement, note: e.target.value }
              })}
              placeholder="Additional settlement terms..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} variant="destructive">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DisputeModal = ({ open, onClose, onSubmit, loading }) => {
  const [form, setForm] = useState({
    reason_code: '',
    reason_text: ''
  });
  
  const handleSubmit = () => {
    if (!form.reason_code) {
      toast.error('Please select a reason');
      return;
    }
    if (!form.reason_text || form.reason_text.length < 20) {
      toast.error('Please provide a detailed explanation (at least 20 characters)');
      return;
    }
    onSubmit(form);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-red-500" />
            Open Dispute
          </DialogTitle>
          <DialogDescription>
            Opening a dispute will freeze the deal and involve Rashii administration for resolution.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-red-950/30 border border-red-900/50">
            <p className="text-sm text-red-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Disputes should be a last resort. Once opened, the deal enters "Dispute Mode" 
                and normal transactions are frozen until resolved.
              </span>
            </p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Dispute Category</label>
            <Select value={form.reason_code} onValueChange={(v) => setForm({ ...form, reason_code: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {DISPUTE_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Dispute Summary</label>
            <Textarea 
              value={form.reason_text}
              onChange={(e) => setForm({ ...form, reason_text: e.target.value })}
              placeholder="Describe the issue in detail. Include dates, communications, and specific contract terms that were violated..."
              rows={5}
            />
            <p className="text-xs text-muted-foreground">
              {form.reason_text.length}/20 minimum characters
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} variant="destructive">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Scale className="w-4 h-4 mr-2" />}
            Open Dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DepositPanel = ({ deal, deposit, userRole, userId, onRefresh, loading }) => {
  const [requestLoading, setRequestLoading] = useState(false);
  const [signLoading, setSignLoading] = useState(false);
  
  const isFormFSigned = deal?.deal_state === 'deal_form_f_signed' || 
                        deal?.deal_state === 'deal_extension_accepted' ||
                        deal?.deal_state === 'deal_closed';
  const isSeller = userId === deal?.seller_id;
  const isBuyer = userId === deal?.buyer_id;
  
  const canRequestRelease = isFormFSigned && 
                            deposit?.deposit_status === 'held' && 
                            (isSeller || isBuyer);
  
  const canSign = deposit?.deposit_status === 'release_requested' &&
                  ((isBuyer && deposit?.instruction?.seller_signed && !deposit?.instruction?.buyer_signed) ||
                   (isSeller && deposit?.instruction?.buyer_signed && !deposit?.instruction?.seller_signed) ||
                   (isSeller && !deposit?.instruction?.seller_signed) ||
                   (isBuyer && !deposit?.instruction?.buyer_signed));
  
  const handleRequestRelease = async (instructionType) => {
    setRequestLoading(true);
    try {
      await api.post(`/deals/${deal.id}/deposit/release-request`, {
        instruction_type: instructionType,
        note: ''
      });
      toast.success('Deposit release requested');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request release');
    } finally {
      setRequestLoading(false);
    }
  };
  
  const handleSign = async () => {
    setSignLoading(true);
    try {
      await api.post(`/deals/${deal.id}/deposit/sign`);
      toast.success('Deposit instruction signed');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign');
    } finally {
      setSignLoading(false);
    }
  };
  
  return (
    <Card className="border-white/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="w-4 h-4 text-primary" />
          Deposit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-primary">
            {formatCurrency(deposit?.deposit_amount || deal?.deposit_amount)}
          </span>
          <DepositStatusBadge status={deposit?.deposit_status || 'held'} />
        </div>
        
        <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm">
          <p className="text-muted-foreground flex items-start gap-2">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Deposit release requires <strong className="text-white">dual signatures</strong> from both buyer and seller.
              Admin override requires a logged reason.
            </span>
          </p>
        </div>
        
        {deposit?.instruction && (
          <div className="space-y-2 p-3 rounded-lg bg-gold-950/20 border border-gold-900/30">
            <p className="text-sm font-medium text-gold-400">Release Instruction Active</p>
            <div className="flex items-center gap-4 text-xs">
              <span className={deposit.instruction.seller_signed ? 'text-green-400' : 'text-muted-foreground'}>
                {deposit.instruction.seller_signed ? '✓' : '○'} Seller
              </span>
              <span className={deposit.instruction.buyer_signed ? 'text-green-400' : 'text-muted-foreground'}>
                {deposit.instruction.buyer_signed ? '✓' : '○'} Buyer
              </span>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          {canRequestRelease && !deposit?.instruction && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => handleRequestRelease(isSeller ? 'RELEASE_TO_SELLER' : 'REFUND_TO_BUYER')}
              disabled={requestLoading || loading}
            >
              {requestLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
              Request Deposit Release
            </Button>
          )}
          
          {canSign && (
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={handleSign}
              disabled={signLoading || loading}
            >
              {signLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
              Sign Release Instruction
            </Button>
          )}
          
          {!isFormFSigned && (
            <p className="text-xs text-center text-muted-foreground">
              Deposit actions available after Form F is signed.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const MessagePanel = ({ dealId, messages, onRefresh, loading }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const handleSend = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await api.post(`/deals/${dealId}/messages`, { message: message.trim() });
      setMessage('');
      toast.success('Message sent');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <Card className="border-white/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-blue-400" />
          Deal Messages
          <Badge variant="outline" className="ml-auto text-xs">
            Immutable
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ScrollArea className="h-48 pr-4">
          {messages?.length > 0 ? (
            <div className="space-y-3">
              {messages.map((msg, i) => (
                <div key={i} className="p-2 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-primary">{msg.sender_role}</span>
                    <span className="text-muted-foreground">{formatRelativeTime(msg.timestamp)}</span>
                  </div>
                  <p className="text-sm">{msg.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No messages yet. Start the conversation.
            </p>
          )}
        </ScrollArea>
        
        <div className="flex gap-2">
          <Textarea 
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            rows={2}
            className="resize-none"
          />
          <Button 
            onClick={handleSend} 
            disabled={sending || !message.trim() || loading}
            className="self-end"
          >
            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Messages are logged permanently and cannot be edited or deleted.
        </p>
      </CardContent>
    </Card>
  );
};

const AdminDisputeResolveModal = ({ open, onClose, onSubmit, loading, deal }) => {
  const [form, setForm] = useState({
    resolution_type: 'RESOLVED',
    outcome: '',
    notes: '',
    compensation: {
      seller_receives: 0,
      buyer_receives: 0
    }
  });
  
  const handleSubmit = () => {
    if (!form.outcome) {
      toast.error('Please provide an outcome description');
      return;
    }
    onSubmit(form);
  };
  
  const depositAmount = deal?.deposit_amount || 0;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Resolve Dispute (Admin)
          </DialogTitle>
          <DialogDescription>
            This action will resolve the dispute and update the deal state.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Resolution Type</label>
            <Select value={form.resolution_type} onValueChange={(v) => setForm({ ...form, resolution_type: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RESOLVED">Resolved (Mutual Agreement)</SelectItem>
                <SelectItem value="REFERRED_EXTERNAL">Referred to External Authority</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Outcome</label>
            <Input 
              value={form.outcome}
              onChange={(e) => setForm({ ...form, outcome: e.target.value })}
              placeholder="Brief outcome description..."
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Deposit Distribution</label>
            <p className="text-xs text-muted-foreground">Total: {formatCurrency(depositAmount)}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Seller Receives</label>
                <Input 
                  type="number"
                  value={form.compensation.seller_receives}
                  onChange={(e) => setForm({ 
                    ...form, 
                    compensation: { ...form.compensation, seller_receives: parseFloat(e.target.value) || 0 }
                  })}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Buyer Receives</label>
                <Input 
                  type="number"
                  value={form.compensation.buyer_receives}
                  onChange={(e) => setForm({ 
                    ...form, 
                    compensation: { ...form.compensation, buyer_receives: parseFloat(e.target.value) || 0 }
                  })}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Resolution Notes</label>
            <Textarea 
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Detailed notes about the resolution..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-primary">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Resolve Dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExtensionResponseModal = ({ open, onClose, deal, onSubmit, loading }) => {
  const [action, setAction] = useState('');
  const [counterDays, setCounterDays] = useState(7);
  const [counterAmount, setCounterAmount] = useState(0);
  
  const handleSubmit = () => {
    if (!action) {
      toast.error('Please select an action');
      return;
    }
    onSubmit({
      action,
      counter_days: action === 'COUNTER' ? counterDays : undefined,
      counter_amount: action === 'COUNTER' ? counterAmount : undefined
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle>Respond to Extension Request</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Your Response</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger>
                <SelectValue placeholder="Select response" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACCEPT">Accept Extension</SelectItem>
                <SelectItem value="COUNTER">Counter Proposal</SelectItem>
                <SelectItem value="REJECT">Reject Extension</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {action === 'COUNTER' && (
            <div className="space-y-3 p-3 rounded-lg bg-gold-950/20 border border-gold-900/30">
              <p className="text-sm font-medium text-gold-400">Counter Proposal</p>
              <div className="flex gap-2 items-center">
                <Input 
                  type="number"
                  value={counterDays}
                  onChange={(e) => setCounterDays(parseInt(e.target.value) || 0)}
                  className="w-20"
                />
                <span className="text-sm">days</span>
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-sm">AED</span>
                <Input 
                  type="number"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(parseFloat(e.target.value) || 0)}
                  placeholder="Compensation"
                />
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !action}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit Response
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TerminationResponseModal = ({ open, onClose, onSubmit, loading }) => {
  const [action, setAction] = useState('');
  
  const handleSubmit = () => {
    if (!action) {
      toast.error('Please select an action');
      return;
    }
    onSubmit({ action });
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle>Respond to Termination Request</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Your Response</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger>
                <SelectValue placeholder="Select response" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACCEPT">Accept Termination</SelectItem>
                <SelectItem value="REJECT">Reject Termination</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {action === 'REJECT' && (
            <div className="p-3 rounded-lg bg-gold-950/20 border border-gold-900/30">
              <p className="text-sm text-gold-400">
                If rejected, the requester may escalate to dispute mode.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !action}
            variant={action === 'ACCEPT' ? 'destructive' : 'default'}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            {action === 'ACCEPT' ? 'Accept Termination' : 'Submit Response'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN COMPONENT ============

const DealTimelinePage = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [deal, setDeal] = useState(null);
  const [property, setProperty] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [resolveDisputeModalOpen, setResolveDisputeModalOpen] = useState(false);
  const [extensionResponseModalOpen, setExtensionResponseModalOpen] = useState(false);
  const [terminationResponseModalOpen, setTerminationResponseModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Event detail sheet
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Derived values
  const userRole = user?.role?.toLowerCase();
  const isSeller = user?.id === deal?.seller_id;
  const isBuyer = user?.id === deal?.buyer_id;
  const isAdmin = userRole === 'admin';
  const isParticipant = isSeller || isBuyer || isAdmin;
  
  const isFormFSigned = ['deal_form_f_signed', 'deal_extension_requested', 'deal_extension_accepted', 
                         'deal_termination_requested', 'deal_dispute_mode', 'deal_closed'].includes(deal?.deal_state);
  const isExtensionRequested = deal?.deal_state === 'deal_extension_requested';
  const isTerminationRequested = deal?.deal_state === 'deal_termination_requested';
  const isDisputeMode = deal?.deal_state === 'deal_dispute_mode';
  const isClosed = deal?.deal_state === 'deal_closed' || deal?.deal_state === 'deal_terminated_mutual';
  
  // Fetch data
  const fetchDeal = useCallback(async () => {
    try {
      setRefreshing(true);
      const [dealRes, depositRes, messagesRes] = await Promise.all([
        api.get(`/deals/${dealId}`),
        api.get(`/deals/${dealId}/deposit`).catch(() => ({ data: null })),
        api.get(`/deals/${dealId}/messages`).catch(() => ({ data: { messages: [] } }))
      ]);
      
      setDeal(dealRes.data);
      setDeposit(depositRes.data);
      setMessages(messagesRes.data?.messages || []);
      
      // Fetch property details
      if (dealRes.data?.property_id) {
        const propRes = await api.get(`/properties/${dealRes.data.property_id}`).catch(() => ({ data: null }));
        setProperty(propRes.data);
      }
      
      // Fetch activity logs for this deal
      try {
        const logsRes = await api.get(`/admin/activity?limit=50`);
        const dealLogs = logsRes.data?.logs?.filter(log => 
          log.entity?.id === dealId || log.entity?.id === dealRes.data?.property_id
        ) || [];
        setTimeline(dealLogs);
      } catch {
        // Non-admin may not have access to activity logs
        setTimeline(dealRes.data?.state_history?.map((h, i) => ({
          timestamp: h.timestamp,
          event_type: 'DEAL_STATE_CHANGED',
          actor: { role: h.actor, user_id: h.actor_id },
          state_transition: { from: h.from, to: h.to }
        })) || []);
      }
      
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('ACCESS_RESTRICTED');
      } else if (err.response?.status === 404) {
        setError('NOT_FOUND');
      } else {
        setError(err.response?.data?.detail || 'Failed to load deal');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dealId]);
  
  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);
  
  // Actions
  const handleExtensionRequest = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/extension/request`, form);
      toast.success('Extension request submitted');
      setExtensionModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit extension request');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleExtensionResponse = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/extension/respond`, form);
      toast.success(`Extension ${form.action.toLowerCase()}`);
      setExtensionResponseModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to extension');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleTerminationRequest = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/termination/request`, form);
      toast.success('Termination request submitted');
      setTerminationModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit termination request');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleTerminationResponse = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/termination/respond`, form);
      toast.success(`Termination ${form.action.toLowerCase()}`);
      setTerminationResponseModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to termination');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDisputeOpen = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/dispute/open`, form);
      toast.success('Dispute opened');
      setDisputeModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open dispute');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDisputeResolve = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/deals/${dealId}/dispute/resolve`, form);
      toast.success('Dispute resolved');
      setResolveDisputeModalOpen(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve dispute');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading deal...</p>
        </div>
      </div>
    );
  }
  
  // Error states
  if (error === 'ACCESS_RESTRICTED') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-900/50">
          <CardContent className="pt-6 text-center">
            <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to view this deal. Only deal participants and administrators can access deal details.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              <Home className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error === 'NOT_FOUND' || !deal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-gold-900/50">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-gold-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Deal Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The deal you're looking for doesn't exist or may have been removed.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              <Home className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const stateConfig = DEAL_STATE_CONFIG[deal.deal_state] || DEAL_STATE_CONFIG.deal_none;
  
  return (
    <div className="min-h-screen bg-background" data-testid="deal-timeline-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold" data-testid="deal-id">
                    Deal {dealId.slice(0, 8).toUpperCase()}
                  </h1>
                  <DealStateBadge state={deal.deal_state} />
                  {deal.form_f_signed_at && (
                    <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30">
                      <FileSignature className="w-3 h-3 mr-1" />
                      Form F Signed
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {property?.title || 'Property'} • {property?.location?.community || property?.location?.area || 'Dubai'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchDeal}
                disabled={refreshing}
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <Progress value={stateConfig.progress} className="h-1.5" />
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Timeline */}
          <div className="lg:col-span-2 space-y-6">
            {/* Deal Summary Card */}
            <Card className="border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Deal Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Accepted Offer</p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(deal.accepted_offer_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Deposit (10%)</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(deal.deposit_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Extensions</p>
                    <p className="text-lg font-bold">{deal.extension_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Created</p>
                    <p className="text-sm">{formatTimestamp(deal.created_at)}</p>
                  </div>
                </div>
                
                {/* Participants */}
                <Separator className="my-4" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-950/50 border border-blue-600/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Seller</p>
                      <p className="font-medium">{isSeller ? 'You' : (isAdmin ? deal.seller_id?.slice(0, 8) : 'Participant')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-950/50 border border-green-600/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Buyer</p>
                      <p className="font-medium">{isBuyer ? 'You' : (isAdmin ? deal.buyer_id?.slice(0, 8) : 'Participant')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Timeline */}
            <Card className="border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Deal Timeline
                </CardTitle>
                <CardDescription>
                  Complete history of deal events (immutable)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  {timeline.length > 0 ? (
                    <div className="space-y-0">
                      {timeline.map((event, i) => (
                        <TimelineEvent 
                          key={i} 
                          event={event} 
                          onViewDetails={setSelectedEvent}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Clock className="w-12 h-12 text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground">No timeline events yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Events will appear as the deal progresses.
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
          
          {/* Right: Actions Panel */}
          <div className="space-y-6">
            {/* Actions Card */}
            <Card className="border-white/10 border-primary/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Actions</CardTitle>
                <CardDescription>
                  {isClosed ? 'Deal is closed' : 
                   isDisputeMode ? 'Deal is in dispute mode' :
                   isFormFSigned ? 'Post-Form F actions available' : 
                   'Awaiting Form F signature'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Buyer Actions */}
                {isBuyer && !isClosed && (
                  <>
                    <ActionButton
                      icon={Timer}
                      onClick={() => setExtensionModalOpen(true)}
                      disabled={!isFormFSigned || isDisputeMode}
                      disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : 'Unavailable in dispute mode'}
                      data-testid="request-extension-btn"
                    >
                      Request Extension
                    </ActionButton>
                    
                    <ActionButton
                      icon={Ban}
                      variant="outline"
                      onClick={() => setTerminationModalOpen(true)}
                      disabled={!isFormFSigned || isDisputeMode}
                      disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : 'Unavailable in dispute mode'}
                      data-testid="request-termination-btn"
                    >
                      Request Termination
                    </ActionButton>
                    
                    <ActionButton
                      icon={Scale}
                      variant="destructive"
                      onClick={() => setDisputeModalOpen(true)}
                      disabled={!isFormFSigned || isDisputeMode || isClosed}
                      disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : isDisputeMode ? 'Already in dispute' : 'Deal closed'}
                      data-testid="open-dispute-btn"
                    >
                      Open Dispute
                    </ActionButton>
                  </>
                )}
                
                {/* Seller Actions */}
                {isSeller && !isClosed && (
                  <>
                    {isExtensionRequested && (
                      <ActionButton
                        icon={CheckCircle2}
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setExtensionResponseModalOpen(true)}
                        data-testid="respond-extension-btn"
                      >
                        Respond to Extension
                      </ActionButton>
                    )}
                    
                    {isTerminationRequested && (
                      <ActionButton
                        icon={CheckCircle2}
                        className="bg-gold-600 hover:bg-gold-700"
                        onClick={() => setTerminationResponseModalOpen(true)}
                        data-testid="respond-termination-btn"
                      >
                        Respond to Termination
                      </ActionButton>
                    )}
                    
                    {!isExtensionRequested && !isTerminationRequested && (
                      <>
                        <ActionButton
                          icon={Timer}
                          onClick={() => setExtensionModalOpen(true)}
                          disabled={!isFormFSigned || isDisputeMode}
                          disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : 'Unavailable in dispute mode'}
                        >
                          Request Extension
                        </ActionButton>
                        
                        <ActionButton
                          icon={Ban}
                          variant="outline"
                          onClick={() => setTerminationModalOpen(true)}
                          disabled={!isFormFSigned || isDisputeMode}
                          disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : 'Unavailable in dispute mode'}
                        >
                          Request Termination
                        </ActionButton>
                        
                        <ActionButton
                          icon={Scale}
                          variant="destructive"
                          onClick={() => setDisputeModalOpen(true)}
                          disabled={!isFormFSigned || isDisputeMode || isClosed}
                          disabledReason={!isFormFSigned ? 'Unavailable until Form F signed' : isDisputeMode ? 'Already in dispute' : 'Deal closed'}
                        >
                          Open Dispute
                        </ActionButton>
                      </>
                    )}
                  </>
                )}
                
                {/* Admin Actions */}
                {isAdmin && (
                  <>
                    {isDisputeMode && (
                      <ActionButton
                        icon={Shield}
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => setResolveDisputeModalOpen(true)}
                        data-testid="resolve-dispute-btn"
                      >
                        Resolve Dispute
                      </ActionButton>
                    )}
                    
                    <ActionButton
                      icon={Eye}
                      variant="outline"
                      onClick={() => navigate(`/property/${deal.property_id}`)}
                    >
                      View Property
                    </ActionButton>
                  </>
                )}
                
                {isClosed && (
                  <div className="p-3 rounded-lg bg-green-950/20 border border-green-900/30 text-center">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-green-400 font-medium">Deal Completed</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Deposit Panel */}
            <DepositPanel 
              deal={deal}
              deposit={deposit}
              userRole={userRole}
              userId={user?.id}
              onRefresh={fetchDeal}
              loading={refreshing}
            />
            
            {/* Deadlines Panel */}
            <DeadlinesMonitor dealId={dealId} />
            
            {/* Messages Panel */}
            <MessagePanel 
              dealId={dealId}
              messages={messages}
              onRefresh={fetchDeal}
              loading={refreshing}
            />
          </div>
        </div>
      </main>
      
      {/* Modals */}
      <ExtensionModal 
        open={extensionModalOpen}
        onClose={() => setExtensionModalOpen(false)}
        onSubmit={handleExtensionRequest}
        loading={actionLoading}
      />
      
      <TerminationModal 
        open={terminationModalOpen}
        onClose={() => setTerminationModalOpen(false)}
        onSubmit={handleTerminationRequest}
        loading={actionLoading}
      />
      
      <DisputeModal 
        open={disputeModalOpen}
        onClose={() => setDisputeModalOpen(false)}
        onSubmit={handleDisputeOpen}
        loading={actionLoading}
      />
      
      <AdminDisputeResolveModal 
        open={resolveDisputeModalOpen}
        onClose={() => setResolveDisputeModalOpen(false)}
        onSubmit={handleDisputeResolve}
        loading={actionLoading}
        deal={deal}
      />
      
      <ExtensionResponseModal 
        open={extensionResponseModalOpen}
        onClose={() => setExtensionResponseModalOpen(false)}
        onSubmit={handleExtensionResponse}
        loading={actionLoading}
        deal={deal}
      />
      
      <TerminationResponseModal 
        open={terminationResponseModalOpen}
        onClose={() => setTerminationResponseModalOpen(false)}
        onSubmit={handleTerminationResponse}
        loading={actionLoading}
      />
      
      {/* Event Details Sheet */}
      <Sheet open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <SheetContent className="bg-background border-white/10">
          <SheetHeader>
            <SheetTitle>Event Details</SheetTitle>
            <SheetDescription>
              {formatTimestamp(selectedEvent?.timestamp)}
            </SheetDescription>
          </SheetHeader>
          
          {selectedEvent && (
            <div className="mt-6 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Event Type</p>
                <p className="font-medium">{selectedEvent.event_type?.replace(/_/g, ' ')}</p>
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground uppercase">Actor</p>
                <p className="font-medium">{selectedEvent.actor?.role || 'System'}</p>
                {selectedEvent.actor?.email && (
                  <p className="text-sm text-muted-foreground">{selectedEvent.actor.email}</p>
                )}
              </div>
              
              {selectedEvent.state_transition?.from && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">State Transition</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{selectedEvent.state_transition.from}</Badge>
                    <ChevronRight className="w-4 h-4" />
                    <Badge variant="outline" className="border-primary text-primary">
                      {selectedEvent.state_transition.to}
                    </Badge>
                  </div>
                </div>
              )}
              
              {selectedEvent.metadata?.reason && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Reason</p>
                  <p className="text-sm">{selectedEvent.metadata.reason}</p>
                </div>
              )}
              
              <div>
                <p className="text-xs text-muted-foreground uppercase">Severity</p>
                <Badge variant="outline">{selectedEvent.severity || 'INFO'}</Badge>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DealTimelinePage;
